// @flow

import React from 'react';
import styled from 'styled-components';
import { Text, Spinner, Icon } from '../';
import { colors } from '../../../stylesheets';

type Props = {
  type?:
    | 'primary'
    | 'secondary'
    | 'primary_outline'
    | 'secondary_outline'
    | 'outline'
    | 'outline_primary'
    | 'primary_white'
    | 'primary_white_filled'
    | 'blue',
  size: 'sm' | 'md' | 'lg',
  disabled?: Boolean,
  text?: String,
  textColor?: String,
  children?: Element,
  loading?: Boolean,
  hoverType?: String,
  width?: String,
  borderRadius?: String,
  border?: String,
  onClick?: Function,
  icon?: String,
  iconSize?: String | Number,
  iconColor?: String,
  iconClassName?: String,
  textSize?: String,
  btnType?: String;
};

const setButtonPadding = ({ size }) =>
  size === 'sm'
    ? '5px 22px'
    : size === 'md'
    ? '5px 30px'
    : size === 'lg'
    ? '5px 76px'
    : '';

const setButtonTypeStyles = ({ type, disabled, hoverType }) =>
  type === 'outline'
    ? {
        backgroundColor: colors['whiteOpacity'],
        border: `1px solid ${colors['lightGray']}`,
        ':hover': {
          backgroundColor: colors[hoverType] || colors['secondary'],
        },
      }
    : type === 'outline_primary'
    ? {
        backgroundColor: colors['secondary'],
        border: `1px solid ${colors['gray']}`,
        ':hover': {
          border: `1px solid ${colors[disabled ? 'gray' : 'white']}`,
        },
      }
    : type === 'primary_outline'
    ? {
        border: `1px solid ${colors[disabled ? 'lighterGray' : 'primary']}`,
        backgroundColor: colors['white'],
        ':hover': {
          border: `1px solid ${
            colors[disabled ? 'lighterGray' : 'primaryHover']
          }`,
          '&>*': {
            color: `${colors.white} !important`,
          },
          backgroundColor: disabled
            ? colors['lighterGray']
            : colors[hoverType] || colors[`lightPrimary`],
        },
      }
    : type === 'primary'
    ? {
        backgroundColor: colors[disabled ? 'lighterGray' : type],
        ':hover': {
          backgroundColor:
            colors[hoverType] ||
            (disabled ? colors['lighterGray'] : colors[`${type}Hover`]),
        },
      }
    : type === 'secondary'
    ? {
        backgroundColor: colors[disabled ? 'lighterGray' : type],
        ':hover': {
          backgroundColor:
            colors[hoverType] ||
            (disabled ? colors['lighterGray'] : colors['black']),
        },
      }
    : type === 'secondary_outline'
    ? {
        border: `1px solid ${colors[disabled ? 'lighterGray' : 'secondary']}`,
        backgroundColor: colors['white'],
        ':hover': {
          border: `1px solid ${colors[disabled ? 'lighterGray' : 'black']}`,
        },
      }
    : type === 'lighterGray'
    ? {
        border: `1px solid ${colors[type]}`,
        backgroundColor: colors[type],
        ':hover': { border: `1px solid ${colors[type]}` },
      }
    : type === 'primary_white'
    ? {
        border: `1px solid ${colors['white']}`,
        backgroundColor: colors['white'],
        padding: '5px 22px',
        ':hover': {
          backgroundColor: colors[hoverType] || colors['secondary'],
        },
      }
    : type === 'primary_white_filled'
    ? {
        border: `1px solid ${colors['lighterGray']}`,
        backgroundColor: colors['white'],
        padding: '5px 22px',
        ':hover': { borderColor: colors['primary'] },
      }
    : type === 'blue'
    ? {
        border: `1px solid ${colors[type]}`,
        backgroundColor: colors['white'],
        ':hover': { borderColor: colors['replyBlue'] },
      }
    : type === 'red'
    ? {
        backgroundColor: colors[disabled ? 'lightGray' : 'red'],
        ':hover': {
          backgroundColor: colors[disabled ? 'lightGray' : 'redHover'],
        },
      }
    : type === 'hover_primary'
    ? {
        backgroundColor: colors[disabled ? 'lightGray' : 'lighterGray'],
        ':hover': {
          backgroundColor: colors[disabled ? 'lightGray' : 'primary'],
        },
      }
    : type === 'green'
    ? {
        backgroundColor: colors[disabled ? 'lighterGray' : 'green'],
        ':hover': {
          backgroundColor:
            colors[disabled ? 'lighterGray' : hoverType || 'hoverGreen'],
        },
      }
    : type === 'hover_green'
    ? {
        backgroundColor: colors[disabled ? 'lighterGray' : 'lighterGray'],
        ':hover': {
          backgroundColor:
            colors[disabled ? 'lighterGray' : hoverType || 'green'],
        },
      }
    : '';

const getTextColor = ({ type, disabled, textColor }) => {
  if (textColor) return textColor;
  if (disabled) return 'lightGray';
  if (type === 'lightGray') return 'lightGray';
  if (type === 'secondary_outline' || type === 'primary_white_filled')
    return 'secondary';
  if (type === 'primary_outline' || type === 'outline_primary')
    return 'primary';
  if (type === 'outline') return 'lightGray';
  return 'white';
};

const StyledButton = styled.button(
  ({
    size,
    type,
    disabled,
    hoverType,
    border,
    borderRadius,
    width,
    height,
    btnType,
  }) => ({
    border: border || 'none',
    outline: 0,
    borderRadius: borderRadius || '0.75rem',
    transition: 'all 0.5s',
    color: 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
    ...(height ? { height } : {}),
    ...(width ? { width } : {}),
    padding: setButtonPadding({ size }),
    ...setButtonTypeStyles({ type, disabled, hoverType }),
    ':focus': {
      outline: 'none',
    },
  })
);

Button.defaultProps = {
  type: 'primary',
  disabled: false,
  size: 'md',
  btnType: "button",
  onClick: () => {},
};

export default function Button(props: Props) {
  const color = getTextColor(props);
  return (
    <StyledButton
      {...props}
      className={`flex-center-center ${props.className}`}
    >
      {props.loading ? (
        <Spinner className="mx-2 w-1_5 h-1_5 thick-border" color={color} />
      ) : (
        <>
          {props.icon && (
            <Icon
              name={props.icon}
              size={props.iconSize || '20'}
              color={props.iconColor || color}
              className={`${props.iconClassName || ''} mx-1`}
            />
          )}
          <Text color={color} className="w-max my-0 mx-1" size={props.textSize}>
            {props.text}
          </Text>
          {props.children}
        </>
      )}
    </StyledButton>
  );
}
